import { useState, useEffect, useCallback } from 'react';
import i18n from 'i18n-js';

import { useAppDispatch } from '@store/hooks/useAppDispatch';
import { getApiKeys, generateApiKeys } from '@reducers/membership';
import { Alert } from '@helpers';
import type { ApiKey } from '@api/domain/types/membership';

export interface UseManageAPIKeysReturn {
  apiKeys: ApiKey[];
  generatedApiKey1: ApiKey | null;
  generatedApiKey2: ApiKey | null;
  isLoading: boolean;
  keyLoading: number | null;
  hasKeys: boolean;
  generateKeys: (keyNums: number[]) => Promise<void>;
  deleteKeys: () => void;
}

export const useManageAPIKeys = (): UseManageAPIKeysReturn => {
  const dispatch = useAppDispatch();

  const [apiKeys, setApiKeys] = useState<ApiKey[]>([]);
  const [generatedApiKey1, setGeneratedApiKey1] = useState<ApiKey | null>(null);
  const [generatedApiKey2, setGeneratedApiKey2] = useState<ApiKey | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [keyLoading, setKeyLoading] = useState<number | null>(null);

  const hasKeys = apiKeys.length > 0 || !!generatedApiKey1 || !!generatedApiKey2;

  const getKeys = useCallback(async () => {
    setIsLoading(true);
    const keys = await dispatch(getApiKeys());
    setApiKeys(keys || []);
    setIsLoading(false);
  }, [dispatch]);

  const generateKeys = useCallback(
    async (keyNums: number[]) => {
      const hasKey1 = keyNums.includes(1);
      const hasKey2 = keyNums.includes(2);

      if (hasKey1 && hasKey2) {
        setIsLoading(true);
      } else {
        setKeyLoading(keyNums.length ? (hasKey1 ? 1 : 0) + (hasKey2 ? 2 : 0) : 3);
      }

      const data = await dispatch(generateApiKeys({ sequenceNumbers: keyNums }));

      if (keyNums.length && data) {
        const key1 = data.apiKeys?.find((key) => key.sequenceNumber === 1);
        const key2 = data.apiKeys?.find((key) => key.sequenceNumber === 2);
        key1 && setGeneratedApiKey1(key1);
        key2 && setGeneratedApiKey2(key2);
      } else {
        setGeneratedApiKey1(null);
        setGeneratedApiKey2(null);
      }

      setKeyLoading(null);
      setIsLoading(false);
    },
    [dispatch],
  );

  const deleteKeys = useCallback(() => {
    if (keyLoading && keyLoading !== 3) {
      return;
    }
    Alert({
      title: i18n.t('Are you sure?'),
      actions: [
        {
          text: i18n.t('No'),
          onPress: () => {},
          style: 'primary',
        },
        {
          text: i18n.t('Yes'),
          onPress: async () => {
            await generateKeys([]);
            await getKeys();
          },
        },
      ],
    });
  }, [keyLoading, generateKeys, getKeys]);

  useEffect(() => {
    getKeys();
  }, []);

  return {
    apiKeys,
    generatedApiKey1,
    generatedApiKey2,
    isLoading,
    keyLoading,
    hasKeys,
    generateKeys,
    deleteKeys,
  };
};
